import React, { useState } from 'react';
import { colors, radii, shadows, spacing } from '../../theme/tokens';
import Icon from './Icon';
import IconButton from './IconButton';

// Semi-transparent glass card matching the example mockups.
// Supports:
//  - title + subtitle + body (children)
//  - optional left image (image prop = url, or imageNode = custom React node)
//  - footer with an "optional" label slot and a round green action button
//  - whole-card hover lift + onClick / onAction
const Card = ({
  title,
  titleIcon,
  subtitle,
  children,
  image,
  imageNode,
  optional,
  action = true,
  actionIcon = 'arrow_forward',
  onAction,
  onClick,
  hoverable = true,
  padding = '22px',
  style,
  bodyStyle,
  ...rest
}) => {
  const [hover, setHover] = useState(false);
  const horizontal = Boolean(image || imageNode);
  const clickable = Boolean(onClick);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: horizontal ? 'row' : 'column',
        gap: horizontal ? '18px' : '12px',
        background: hover && hoverable ? colors.glassStrong : colors.glass,
        border: `1px solid ${hover && hoverable ? colors.glassBorderStrong : colors.glassBorder}`,
        borderRadius: radii.lg,
        padding,
        color: colors.text,
        boxShadow: hover && hoverable ? shadows.lg : shadows.md,
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease',
        transform: hover && hoverable ? 'translateY(-4px)' : 'none',
        cursor: clickable ? 'pointer' : 'default',
        textDecoration: 'none',
        ...style
      }}
      {...rest}
    >
      {horizontal && (
        <div
          style={{
            flexShrink: 0,
            width: 120,
            height: 120,
            borderRadius: radii.md,
            overflow: 'hidden',
            background: colors.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {imageNode || (
            <img
              src={image}
              alt={title || ''}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, gap: spacing.xs }}>
        {title && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {titleIcon && <Icon name={titleIcon} size={20} color={colors.parGreenBright} />}
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: colors.text }}>{title}</h3>
          </div>
        )}
        {subtitle && (
          <div style={{ fontSize: 13, color: colors.textMuted }}>{subtitle}</div>
        )}

        <div style={{ fontSize: 14, color: colors.textMuted, lineHeight: 1.5, ...bodyStyle }}>
          {children}
        </div>

        {(optional || action) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              marginTop: 'auto',
              paddingTop: '14px'
            }}
          >
            <span style={{ fontSize: 13, color: colors.textFaint }}>{optional}</span>
            {action && (
              <IconButton
                icon={actionIcon}
                size={38}
                title={typeof title === 'string' ? title : 'Open'}
                onClick={(e) => {
                  if (onAction) {
                    e.stopPropagation();
                    onAction(e);
                  }
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Card;
